import React, { useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { AllergiesKind, FoodItemFragment } from "@/gql/graphql";
import useNumberInputState from "@/hooks/useNumberInputState";
import { colors } from "@/theme/overrides/colors";
import { getPortionWeightGrams } from "@/utils/food";
import { round } from "@/utils/number";
import { FragmentType } from "@/utils/type";

import FoodItemIcon from "./FoodItemIcon";

interface Props {
  foodId: string;
  food?: FoodItemFragment;
  defaultFoodPortionId?: string;
  defaultAmount?: number;
  open: boolean;
  title?: string;
  onClose: () => void;
  onAddFood: (
    food: FoodItemFragment,
    foodPortion: FragmentType<typeof foodAddPortionDetailsFragment>,
    amount: number
  ) => void;
  clientAllergies?: AllergiesKind[];
}

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: 450,
    // Never let the fixed width overflow a narrow screen.
    maxWidth: "calc(100vw - 32px)",
    paddingBottom: "32px",
  },
  title: {
    padding: theme.spacing(3, 4),
  },
  titleText: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.dark,
  },
  closeButton: {
    color: colors.gray,
    backgroundColor: "#E8EEF6",
  },
  content: {
    padding: 0,
  },
  nutrientColumn: {
    margin: theme.spacing(3, -4),
    width: `calc(100% + ${theme.spacing(8)})`,
  },
  foodName: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.dark,
  },
  nutrient: {
    position: "relative",
    padding: theme.spacing(4),
  },
  hdivider: {
    width: "100%",
    backgroundColor: "#EDF0F5",
  },
  vdivider: {
    backgroundColor: "#EDF0F5",
  },
  nutrientCircle: {
    position: "absolute",
    top: 20,
    borderRadius: "50%",
    border: "5px solid #F76C41",
    width: "108px",
    height: "108px",
    [theme.breakpoints.down("sm")]: {
      top: 8,
      borderWidth: 4,
      width: "68px",
      height: "68px",
    },
  },
  nutrientName: {
    fontSize: 14,
    color: colors.gray,
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },
  nutrientValue: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.dark,
    zIndex: 10,
    [theme.breakpoints.down("sm")]: {
      fontSize: 17,
    },
  },
  nutrientPercent: {
    fontSize: 16,
    fontWeight: 700,
    color: "#2D87BB",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
    },
  },
  measurement: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.gray,
  },
  portionField: {
    width: "100%",
  },
  amountField: {
    width: "100%",
  },
  amountButton: {
    width: "100%",
    height: "100%",
    border: "1px solid #EDF0F5",
    color: colors.gray,
    backgroundColor: colors.light,
  },
  decAmountButton: {
    borderRight: 0,
  },
  incAmountButton: {
    borderLeft: 0,
  },
}));

const foodAddPortionDetailsFragment = graphql(`
  fragment FoodAddPortionDetails on FoodPortion {
    id
    foodId
    amount
    unit
    unitWithModifier
    weightGrams
  }
`);

const addFoodPortionDialogQuery = graphql(`
  query AddFoodPortionDialog($id: ID!) {
    food(id: $id) {
      ...FoodItem
      portions {
        ...FoodAddPortionDetails
      }
    }
  }
`);

const AddFoodPortionDialog: React.FC<Props> = ({
  open,
  onClose,
  onAddFood,
  foodId,
  food: initialFood,
  title,
  defaultFoodPortionId,
  defaultAmount,
}) => {
  const { classes, cx } = useStyles();
  const [portionIndex, setPortionIndex] = useState<number>(0);
  const [amountNum, amountStr, setAmount] = useNumberInputState({
    default: defaultAmount,
    min: 0,
    maxDecimalPlace: 2,
  });

  const [fetchFood, { data }] = useLazyQuery(addFoodPortionDialogQuery, {
    variables: { id: foodId },
  });

  useEffect(() => {
    if (open && initialFood == null) fetchFood();
  }, [fetchFood, initialFood, open]);

  const food = initialFood ?? data?.food;

  useEffect(() => {
    if (!open || !food) return;

    const defaultPortionIndex = defaultFoodPortionId
      ? food.portions.findIndex((p) => p.id === defaultFoodPortionId)
      : 0;
    const nextPortionIndex =
      defaultPortionIndex === -1 ? 0 : defaultPortionIndex;

    setPortionIndex(nextPortionIndex);
    setAmount(defaultAmount ?? food.portions[nextPortionIndex]?.amount ?? 0);
  }, [defaultAmount, defaultFoodPortionId, food, open, setAmount]);

  const renderDialogTitle = () => {
    return (
      <DialogTitle className={classes.title}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Typography className={classes.titleText}>
            {title ?? "Add Item Portions"}
          </Typography>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
    );
  };

  // Empty placeholder dialog while still loading
  if (food == null)
    return (
      <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
        {renderDialogTitle()}
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={4}
            minHeight={420} // blaze it
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );

  // A food can come back from search with no portions. Selecting/adding a
  // portion is impossible in that case, and computing the weight would throw
  // and white-screen the whole page (this component renders for every search
  // result, even while closed), so bail out gracefully.
  const selectedPortion = food.portions[portionIndex];
  if (selectedPortion == null)
    return (
      <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
        {renderDialogTitle()}
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={4}
            minHeight={420}
          >
            <Typography className={classes.measurement}>
              No portions available for this item.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );

  const weight = getPortionWeightGrams(selectedPortion, amountNum);
  const weightPercent = weight / food.epWeightGrams;

  const macroValue = (macro: number | null | undefined) => {
    if (macro == null)
      return (
        <>
          <Typography className={classes.nutrientPercent}>&nbsp;</Typography>
          <Typography className={classes.nutrientValue}>N/A</Typography>
        </>
      );

    const percentage = Math.round((macro / food.epWeightGrams) * 100);
    return (
      <>
        <Typography className={classes.nutrientPercent}>
          {percentage}%
        </Typography>
        <Typography className={classes.nutrientValue}>
          {round(macro * weightPercent, 2)}g
        </Typography>
      </>
    );
  };

  const addFood = () => {
    onAddFood(food, food.portions[portionIndex], amountNum);
    setPortionIndex(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.paper,
      }}
    >
      {renderDialogTitle()}
      <DialogContent className={classes.content}>
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" alignItems="center" p={2}>
            <FoodItemIcon category={food.category} background={false} />
            <Typography className={classes.foodName}>{food.name}</Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Divider className={classes.hdivider} />
            <Box display="flex" flexDirection="row">
              <Box
                display="flex"
                flexDirection="column"
                sx={{ position: "relative", minWidth: "30%", maxWidth: "30%" }}
                alignItems="center"
                justifyContent="center"
                padding={{ xs: 1.5, sm: 4 }}
              >
                <div className={classes.nutrientCircle}></div>
                <Typography className={classes.nutrientPercent}>
                  &nbsp;
                </Typography>
                <Typography className={classes.nutrientValue}>
                  {Math.round(food.energyKcal * weightPercent)}
                </Typography>
                <Typography className={classes.nutrientName}>
                  Calories
                </Typography>
              </Box>
              <Divider
                className={classes.vdivider}
                orientation="vertical"
                flexItem
              />
              <Grid container>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ position: "relative" }}
                    alignItems="center"
                    padding={{ xs: 1, sm: 4 }}
                  >
                    {macroValue(food.carbohydrateGrams)}
                    <Typography className={classes.nutrientName}>
                      Carbohydrate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ position: "relative" }}
                    alignItems="center"
                    padding={{ xs: 1, sm: 4 }}
                  >
                    {macroValue(food.fatGrams)}
                    <Typography className={classes.nutrientName}>
                      Fat
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ position: "relative" }}
                    alignItems="center"
                    padding={{ xs: 1, sm: 4 }}
                  >
                    {macroValue(food.proteinGrams)}
                    <Typography className={classes.nutrientName}>
                      Protein
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Box mb={5} />
          <Box display="flex" flexDirection="column" margin={3}>
            <Typography className={classes.measurement}>Measurement</Typography>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  className={classes.portionField}
                  select
                  SelectProps={{
                    native: true, // native select is used to avoid triggering ClickAwayListener on focus of this field
                  }}
                  value={portionIndex}
                  onChange={(event) => {
                    const index = parseInt(event.target.value);
                    setPortionIndex(index);
                    setAmount(food.portions[index].amount);
                  }}
                >
                  {food.portions.map((p, index) => (
                    <option key={index} value={index}>
                      {p.unitWithModifier}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} container>
                <Grid item xs={8}>
                  <TextField
                    className={classes.amountField}
                    value={amountStr}
                    onChange={(e) => {
                      if (e.target.value.startsWith("-")) return;
                      setAmount(e.target.value);
                    }}
                    type="number"
                  />
                </Grid>
                <Grid item xs={2}>
                  <ButtonBase
                    className={cx(
                      classes.amountButton,
                      classes.decAmountButton
                    )}
                    onClick={() => setAmount(amountNum - 1)}
                  >
                    <RemoveIcon />
                  </ButtonBase>
                </Grid>
                <Grid item xs={2}>
                  <ButtonBase
                    className={cx(
                      classes.amountButton,
                      classes.incAmountButton
                    )}
                    onClick={() => setAmount(amountNum + 1)}
                  >
                    <AddIcon />
                  </ButtonBase>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box mb={5} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              sx={{ textTransform: "initial" }}
              size="large"
              onClick={addFood}
              disabled={amountNum <= 0}
            >
              Add to diet
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddFoodPortionDialog;
